import { z } from "zod";

/**
 * Mail Settings (Masters, user 2026-10-15): one sender per processing plant. Every mail a plant's
 * work causes goes out through that plant's own account: a user's invite, login code and reset
 * code from the user's Mail plant, an arrival's e-mail from the arrival's plant. With no row, or an
 * inactive one, the send is refused; nothing falls back to another plant's account.
 *
 * Senders: Zoho Mail's API and the Gmail API go over HTTPS, the only way out of the prod droplet
 * (DigitalOcean blocks SMTP ports). SMTP covers a Gmail app password or Zoho SMTP on hosts that
 * allow it.
 */
export const MAIL_DRIVERS = ["zoho_api", "gmail_api", "smtp"] as const;
export type MailDriver = (typeof MAIL_DRIVERS)[number];

export const MAIL_DRIVER_LABELS: Record<MailDriver, string> = {
  zoho_api: "Zoho Mail (API)",
  gmail_api: "Gmail (API)",
  smtp: "SMTP (Gmail app password / Zoho SMTP)",
};

/** Zoho's data centres: the accounts host and the mail API host must be the same region. */
export const ZOHO_REGIONS = {
  in: {
    label: "India (zoho.in)",
    accountsUrl: "https://accounts.zoho.in",
    apiUrl: "https://mail.zoho.in",
  },
  com: {
    label: "Global (zoho.com)",
    accountsUrl: "https://accounts.zoho.com",
    apiUrl: "https://mail.zoho.com",
  },
  eu: {
    label: "Europe (zoho.eu)",
    accountsUrl: "https://accounts.zoho.eu",
    apiUrl: "https://mail.zoho.eu",
  },
} as const;
export type ZohoRegion = keyof typeof ZOHO_REGIONS;
export const ZOHO_REGION_KEYS = Object.keys(ZOHO_REGIONS) as ZohoRegion[];

/** One-click SMTP settings; the form fills host, port and SSL from these. */
export const SMTP_PRESETS = {
  gmail: { label: "Gmail", host: "smtp.gmail.com", port: 465, secure: true },
  zoho_in: {
    label: "Zoho (India)",
    host: "smtp.zoho.in",
    port: 465,
    secure: true,
  },
  zoho_com: {
    label: "Zoho (Global)",
    host: "smtp.zoho.com",
    port: 465,
    secure: true,
  },
} as const;

const opt = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => v || null);
/** A secret field: blank or omitted keeps the stored value; it is never read back. */
const secret = z
  .string()
  .trim()
  .max(4000)
  .optional()
  .transform((v) => v || undefined);

const mailConfigBase = z.object({
  facilityId: z.number({ message: "Pick the plant" }).int().positive(),
  driver: z.enum(MAIL_DRIVERS, { message: "Pick how this plant sends mail" }),
  fromName: opt(120),
  fromAddress: z.email({ message: "Enter the From e-mail address" }),
  replyTo: z
    .union([
      z.email({ message: "Enter a valid Reply-to address" }),
      z.literal(""),
    ])
    .nullish()
    .transform((v) => v || null),
  smtpHost: opt(200),
  smtpPort: z.coerce.number().int().min(1).max(65535).nullish(),
  smtpSecure: z.boolean().default(true),
  smtpUser: opt(200),
  oauthClientId: opt(300),
  zohoRegion: z
    .enum(ZOHO_REGION_KEYS as [ZohoRegion, ...ZohoRegion[]])
    .default("in"),
  /** Optional: looked up from the From address when blank. */
  zohoAccountId: opt(60),
  smtpPassword: secret,
  oauthClientSecret: secret,
  oauthRefreshToken: secret,
  isActive: z.boolean().default(true),
});

/**
 * Required fields by driver. Secrets are checked by the server against what is already stored, so
 * an edit that leaves a secret blank (keep it) still passes here.
 */
const requireForDriver = (
  v: z.infer<typeof mailConfigBase>,
  ctx: z.RefinementCtx
) => {
  const need = (field: keyof typeof v, message: string) => {
    if (v[field] === null || v[field] === undefined || v[field] === "")
      ctx.addIssue({ code: "custom", path: [field], message });
  };
  if (v.driver === "smtp") {
    need("smtpHost", "Enter the SMTP server");
    need("smtpPort", "Enter the SMTP port");
    need("smtpUser", "Enter the SMTP user name");
  } else {
    need("oauthClientId", "Enter the OAuth client ID");
  }
};

export const mailConfigInput = mailConfigBase.superRefine(requireForDriver);
export type MailConfigInput = z.infer<typeof mailConfigInput>;

export const mailConfigResponse = z.object({
  id: z.number(),
  facilityId: z.number(),
  plantCode: z.string(),
  plantName: z.string(),
  driver: z.enum(MAIL_DRIVERS),
  fromName: z.string().nullable(),
  fromAddress: z.string(),
  replyTo: z.string().nullable(),
  smtpHost: z.string().nullable(),
  smtpPort: z.number().nullable(),
  smtpSecure: z.boolean(),
  smtpUser: z.string().nullable(),
  oauthClientId: z.string().nullable(),
  zohoRegion: z.enum(ZOHO_REGION_KEYS as [ZohoRegion, ...ZohoRegion[]]),
  zohoAccountId: z.string().nullable(),
  /** Which secrets are stored. The values themselves never leave the server. */
  hasSmtpPassword: z.boolean(),
  hasOauthClientSecret: z.boolean(),
  hasOauthRefreshToken: z.boolean(),
  isActive: z.boolean(),
  lastTestAt: z.string().nullable(),
  lastTestOk: z.boolean().nullable(),
  lastTestError: z.string().nullable(),
  updatedAt: z.string(),
});
export type MailConfigResponse = z.infer<typeof mailConfigResponse>;

export const mailConfigTestInput = z.object({
  /** Defaults to the caller's own address. */
  to: z.email({ message: "Enter a valid e-mail address" }).optional(),
});
export const mailConfigTestResponse = z.object({
  ok: z.boolean(),
  to: z.string(),
  error: z.string().nullable(),
});
